import clsx from 'clsx'

import { normalizeImage } from '@/lib/contentful/normalizers/image'

import { isDevelopment } from '@/utils/isDevelopment'

import { Button, ButtonContentProps } from '@/components/Button'
import { ContentfulRichText } from '@/components/Contentful/common/ContentfulRichText'
import { Image } from '@/components/Image'
import { Warning } from '@/components/Warning'

import { useResource } from '../useResource'

type Props = {
	className?: string
}

export function ResourceHero({ className }: Props) {
	const entry = useResource()

	if ('error' in entry) {
		if (isDevelopment()) return <Warning className={className}>{entry.error}</Warning>
		return null
	}

	const { headline, subheading, cta, image, backgroundOptions } = entry?.data?.hero ?? {}

	const {
		backgroundImage,
		gradient,
		solidColor = '#121118',
		textColor = 'White'
	} = backgroundOptions ?? {}
	const heroImg = normalizeImage(image)
	const hasHeroImage = Boolean(heroImg?.url)

	if (hasHeroImage) {
		return (
			<div
				data-testid="resources-hero"
				className={clsx(className, 'px-[30px] py-[100px]')}
				style={{
					backgroundImage: (backgroundImage?.url ? `url(${backgroundImage?.url})` : gradient) || '',
					backgroundPosition: 'center',
					backgroundSize: 'cover',
					backgroundRepeat: 'no-repeat',
					backgroundColor: solidColor ?? ''
				}}
			>
				<div className="grid grid-flow-row md:grid-flow-col xl:w-[1156px] md:gap-0 m-auto place-items-center md:place-items-start">
					<div className="grid grid-flow-row pb-5 h-max xl:w-[548px] self-center place-items-center md:place-items-start">
						<h1
							className={clsx(
								'font-bold text-[41px] lead-[50px] text-center md:text-start mt-12',
								textColor === 'White' ? 'text-white' : 'text-bc-black'
							)}
						>
							{headline}
						</h1>
						<ContentfulRichText
							className="pb-[40px] text-center md:text-start"
							textColor={textColor === 'White' ? 'white' : 'default'}
							field={{ data: subheading }}
						/>
						{cta?.link?.url && (
							<Button
								link={{ href: cta?.link?.url, target: '_self' }}
								variant={(cta.variant as ButtonContentProps['variant']) ?? 'filled'}
								color={(cta.color as ButtonContentProps['color']) ?? 'primary'}
								showIcon={false}
								className="w-fit"
							>
								{cta.displayText}
							</Button>
						)}
					</div>
					{heroImg?.url && (
						<Image
							className="md:justify-self-end justify-self-center pt-15 md:pt-0"
							src={heroImg?.url}
							alt="hero-img"
							width={548}
							height={548}
						/>
					)}
				</div>
			</div>
		)
	}

	return (
		<div
			data-testid="resources-hero"
			className={clsx(className, 'px-[30px] py-[100px]')}
			style={{
				backgroundImage: (backgroundImage?.url ? `url(${backgroundImage?.url})` : gradient) || '',
				backgroundPosition: 'center',
				backgroundSize: 'cover',
				backgroundRepeat: 'no-repeat',
				backgroundColor: solidColor ?? ''
			}}
		>
			<div className="grid grid-flow-row xl:w-[768px] md:gap-0 m-auto place-items-center">
				<h1
					className={clsx(
						'font-bold text-[41px] lead-[50px] text-center mt-12',
						textColor === 'White' ? 'text-white' : 'text-bc-black'
					)}
				>
					{headline}
				</h1>
				<ContentfulRichText
					className="pb-[40px] text-center"
					textColor={textColor === 'White' ? 'white' : 'default'}
					field={{ data: subheading }}
				/>
				{cta?.link?.url && (
					<Button
						link={{ href: cta?.link?.url, target: '_self' }}
						variant={(cta.variant as ButtonContentProps['variant']) ?? 'filled'}
						color={(cta.color as ButtonContentProps['color']) ?? 'primary'}
						showIcon={false}
						className="w-fit"
					>
						{cta.displayText}
					</Button>
				)}
			</div>
		</div>
	)
}
